import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axiosClient from "../../../../../config/axios.js";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import Spinner from "react-bootstrap/Spinner";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "../../../../../components/panel/panel";
import { getStampFile } from "../../../../../services/CatalogService";
import moment from "moment-timezone";

function DatatableStampedInvoices({
  razonSocialFacturacion,
  tipoOrden,
  refreshKey,
  onTotalsChange,
}) {
  const { id } = useParams();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("issued_at");
  const [sortDirection, setSortDirection] = useState("desc");

  const fetchData = async (
    currentPage = 1,
    search = "",
    sortField,
    sortDirection
  ) => {
    try {
      const response = await axiosClient.get(`/stamped-invoices/datatable`, {
        params: {
          ...(tipoOrden === "vial" ? { orderVialId: id } : { orderId: id }),
          start: (currentPage - 1) * rowsPerPage,
          length: rowsPerPage,
          search: search,
          sort: sortField,
          dir: sortDirection,
        },
      });
      setData(response.data);
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (id) {
      setLoading(true);
      fetchData(currentPage, searchValue, sortField, sortDirection);
    }
  }, [id, currentPage, rowsPerPage, sortField, sortDirection, refreshKey]);

  useEffect(() => {
    if (onTotalsChange) {
      const totalFacturado = data?.data?.reduce(
        (acc, row) => acc + (Number(row.total) || 0),
        0
      );
      onTotalsChange(totalFacturado);
    }
  }, [data, onTotalsChange]);

  const handleDownload = async (uuid_stamp) => {
    try {
      // Llamamos al servicio que obtiene el archivo de la factura por uuid_stamp
      const result = await getStampFile(uuid_stamp, razonSocialFacturacion);
      const fileUrl = result?.file?.url;

      if (!fileUrl) {
        Swal.fire({
          icon: "warning",
          title: "Advertencia",
          text: "No se pudo obtener el archivo de la factura o puede que aún no esté lista.",
        });
        return;
      }

      // Crear un enlace para descargar el archivo
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = `${result.file.name || uuid_stamp}.${
        result.file.extension || "pdf"
      }`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error al descargar la factura:", error);
      Swal.fire({
        icon: "error",
        title: "Error al descargar",
        text: "Ocurrió un error al intentar descargar la factura.",
      });
    }
  };

  const columns = [
    {
      name: "Descargar",
      button: true,
      cell: (row) =>
        row.id_facturacion_stamp ? (
          <button
            type="button"
            onClick={() => handleDownload(row.id_facturacion_stamp)}
            className="btn btn-primary btn-sm"
          >
            <i className="fa fa-download me-1" /> Descargar Factura
          </button>
        ) : null,
    },
    {
      name: "Factura registrada por",
      selector: (row) =>
        row.creator_invoice
          ? (
              row.creator_invoice.name +
              " " +
              row.creator_invoice.first_surname +
              " " +
              (row.creator_invoice.second_surname || "")
            ).trim()
          : "Sin registrar",
      sortable: false,
      width: "220px",
    },
    {
      name: "UUID Stamp SW",
      selector: (row) => row.uuid_stamp,
      sortable: true,
      sortField: "uuid_stamp",
    },
    {
      name: "Cliente",
      selector: (row) => row.customer_name,
      sortable: true,
      sortField: "customer_name",
    },
    {
      name: "RFC",
      selector: (row) => row.customer_rfc,
      sortable: true,
      sortField: "customer_rfc",
      width: "150px",
    },
    {
      name: "Fecha de emisión",
      selector: (row) =>
        row.issued_at
          ? moment(row.issued_at)
              .tz("America/Mexico_City")
              .format("DD/MM/YYYY HH:mm:ss")
          : "Sin fecha",
      sortable: true,
      sortField: "issued_at",
    },
    {
      name: "Subtotal",
      selector: (row) => `$${parseFloat(row.subtotal).toFixed(2)}`,
      sortable: true,
      sortField: "subtotal",
      width: "100px",
    },
    {
      name: "IVA",
      selector: (row) => `$${parseFloat(row.iva).toFixed(2)}`,
      sortable: true,
      sortField: "iva",
      width: "80px",
    },
    {
      name: "Retención",
      selector: (row) => {
        const value = parseFloat(row.retencion);
        const safeValue = isNaN(value) ? 0 : value;
        return `$${safeValue.toFixed(2)}`;
      },
      sortable: true,
      sortField: "retencion",
      width: "80px",
    },
    {
      name: "Total",
      selector: (row) => `$${parseFloat(row.total).toFixed(2)}`,
      sortable: true,
      sortField: "total",
      width: "100px",
    },
    {
      name: "Estatus",
      selector: (row) => row.status,
      sortable: true,
      sortField: "status",
      width: "100px",
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        color: "#202124",
        fontSize: "14px",
        backgroundColor: "#EEEEEE",
        border: "none",
      },
    },
  };

  const paginationComponentOptions = {
    rowsPerPageText: "Filas por página",
    rangeSeparatorText: "de",
    selectAllRowsItem: false,
    selectAllRowsItemText: "Todos",
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      setLoading(true);
      setCurrentPage(1);
      fetchData(1, searchValue, sortField, sortDirection);
    }
  };

  return (
    <div>
      <div className="row mb-3">
        <div className="col-xl-12">
          <Panel>
            <PanelBody>
              <DataTable
                className="table"
                columns={columns}
                data={data.data}
                progressPending={loading}
                sortServer
                pagination
                paginationServer
                paginationPerPage={rowsPerPage}
                paginationTotalRows={data.recordsTotal}
                paginationDefaultPage={currentPage}
                subHeader
                customStyles={customStyles}
                pointerOnHover
                paginationComponentOptions={paginationComponentOptions}
                noDataComponent="Sin información por mostrar"
                progressComponent={
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </Spinner>
                }
                subHeaderComponent={
                  <div className="row">
                    <div className="col-sm-12 col-md-12">
                      <div className="input-group mb-3">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Buscar..."
                          value={searchValue}
                          onChange={(e) => setSearchValue(e.target.value)}
                          onKeyPress={handleKeyPress}
                        />
                        <div
                          className="input-group-text btn btn-info"
                          onClick={() => {
                            setLoading(true);
                            setCurrentPage(1);
                            fetchData(1, searchValue, sortField, sortDirection);
                          }}
                        >
                          <i className="fa fa-search"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                }
                onChangePage={(page) => setCurrentPage(page)}
                onChangeRowsPerPage={(newPerPage) => {
                  setRowsPerPage(newPerPage);
                  setCurrentPage(1);
                }}
                onSort={(column, direction) => {
                  setSortField(column.sortField);
                  setSortDirection(direction);
                  setCurrentPage(1);
                }}
              />
            </PanelBody>
          </Panel>
        </div>
      </div>
    </div>
  );
}

export default DatatableStampedInvoices;
